
import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { toast } from "sonner";

export default function AppLayout() {
    const { user, isLoading, tenantType, authStatus, isInitialLoad } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    // Check if the user is trying to access admin routes
    const isAdminRoute = location.pathname.startsWith("/ops");

    useEffect(() => {
        // If user is authenticated and the path is just "/"
        if (user && location.pathname === "/") {
            // Redirect based on tenant type
            if (tenantType === "admin") {
                navigate("/ops");
            } else {
                navigate("/dashboard");
            }
        }
    }, [user, location, tenantType, navigate]);

    // Show loading state only during initial load
    if ((isLoading || authStatus.isChecking) && isInitialLoad) {
        return (
            <div className="flex h-screen w-full items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    // If not authenticated, redirect to login
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // If user is authenticated but not registered or authorized
    if (!authStatus.isAuthorized) {
        return <Navigate to="/error" replace />;
    }

    // If trying to access admin routes but not an admin
    if (isAdminRoute && tenantType !== "admin") {
        toast.error("You don't have permission to access this area");
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <SidebarProvider>
            <div className="flex min-h-screen w-full">
                <Sidebar />
                <div className="flex flex-1 flex-col">
                    <Topbar />
                    <main className="flex-1 p-4 md:p-6">
                        <div className="mx-auto w-full max-w-[1200px] px-4 @lg:px-6 @xl:px-12 @2xl:px-20 @3xl:px-24 space-y-6">
                            <Outlet />
                        </div>
                    </main>
                </div>
            </div>
        </SidebarProvider>
    );
}
